<script>
  import { isMobile } from '$api/stores/app'
  import { _, basePath } from '$api/stores/localization'
  import { currentUser, hasSyncedContacts, mySentFollowRequestIds } from '$api/stores/user'
  import { page } from '$app/stores'
  import ConditionalComponent from '$components/ConditionalComponent.svelte'
  import PageContent from '$components/PageContent.svelte'
  import PageHeader from '$components/PageHeader.svelte'
  import StickyOnScroll from '$components/StickyOnScroll.svelte'
  import SyncOrInviteButton from '$components/buttons/SyncOrInviteButton.svelte'
  import ScrollableTabs from '$components/tabs/ScrollableTabs.svelte'
  import { BASE_URL } from '$fixtures/constants'
  import { TrackingContext, TrackingPage } from '$fixtures/enums/Tracking'
  import { environment } from '$lib/environment'
  import { navigate } from '$utils/navigate'
  import {
    userFollowRequestsPath,
    usersFollowersPath,
    usersFollowingPath,
    usersPath,
    usersSimilarPath,
    usersSyncedContactsPath,
  } from '$utils/routes'
  import { addTrackingContext } from '$utils/tracking'

  export let data

  addTrackingContext(TrackingContext.Page, TrackingPage.Users)

  const { region } = data

  $: pages = [
    {
      text: $_('Who To Follow'),
      href: usersPath($basePath),
    },
    {
      text: $_('Synced Friends'),
      href: usersSyncedContactsPath($basePath),
      condition: !!$currentUser && $hasSyncedContacts,
    },
    {
      text: $_('Similar Users'),
      href: usersSimilarPath($basePath),
      condition: !!$currentUser,
    },
    {
      text: $_('Following'),
      href: usersFollowingPath($basePath),
    },
    {
      text: $_('Followers'),
      href: usersFollowersPath($basePath),
    },
    {
      text: $_('Follow Requests'),
      href: userFollowRequestsPath($basePath),
      condition: !!$mySentFollowRequestIds.length,
    },
  ]

  $: tabs = pages
    .filter(p => p.condition === undefined || p.condition)
    .map(p => ({ ...p, value: p.href }))
</script>

<svelte:head>
  <title>{$_('Users')} - PlayPilot</title>
  <meta name="description" content={$_('Meta Info - Description')} />
  <meta name="robots" content="noindex" />
  <meta property="og:url" content={`${BASE_URL}/${region}/users`} />
  <meta property="og:title" content={$_('Meta Info - Users')} />
  <meta property="og:description" content={$_('Meta Info - Description')} />
  <meta property="og:image" content={`${BASE_URL}/images/previews/movies-and-shows.png`} />
  <meta property="og:image:width" content="1200" />
  <meta property="og:image:height" content="630" />
</svelte:head>

<PageHeader padding={false} flush>
  <div class="header-wrapper">
    <ConditionalComponent component={StickyOnScroll} condition={$isMobile}>
      <div class="header">
        <div class="heading">
          <h1 class="m-0">{$_('Users')}</h1>
        </div>

        <div class="buttons">
          <ScrollableTabs
            {tabs}
            on:change={({ detail }) => navigate(detail.tab.value, { noScroll: !$isMobile })}
            value={$page.url.pathname}
            unlimited
            buttons
            size="auto"
            align={$isMobile ? '' : 'start'} />

          <div class="push">
            <SyncOrInviteButton />
          </div>
        </div>
      </div>
    </ConditionalComponent>
  </div>
</PageHeader>

<PageContent flush>
  <div class:app={environment.IS_APP}>
    <slot />
  </div>
</PageContent>

<style lang="scss">
  .header {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    width: 100%;
    padding: margin(1) var(--gutter);
    background: $bg-base;

    @include breakpoint(tablet) {
      flex-direction: column;
      padding: 2rem var(--gutter) 0;
    }
  }

  .header-wrapper {
    z-index: 1;
    position: relative;
    width: 100%;

    :global(.sticky) {
      z-index: 10;
      top: var(--navigation-height);
    }
  }

  .heading {
    display: flex;
    align-items: center;
    justify-content: space-between;

    @include breakpoint(tablet) {
      margin-bottom: margin(1);
    }
  }

  .buttons {
    display: flex;
    flex-direction: column;
    margin-top: margin(0.5);
    gap: margin(0.5);
    white-space: nowrap;

    @include breakpoint(tablet) {
      flex-direction: row;
      margin-top: margin(1.5);
    }

    :global(.scrollable) {
      width: 100%;
    }
  }

  .push {
    @include breakpoint(tablet) {
      margin-left: auto;
    }
  }

  .app {
    padding-bottom: var(--drawer-height, 0px);
  }
</style>
